import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useAuthStore } from '@/stores/authStore';
import { formatDate } from '@/utils/locale';
import {
  isCertificateOverrideForbiddenError,
  SKILLS_CERTIFICATE_OVERRIDE_PERMISSION,
  type CertificateBlockedItem,
} from './certificateGateError';

const REASON_MAX = 2000;

export interface CertificateOverrideDialogProps {
  open: boolean;
  submitting: boolean;
  blocked: CertificateBlockedItem[];
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

function blockedLine(item: CertificateBlockedItem, t: TFunction): string {
  if (item.status === 'expired') {
    return t('certificateGate.expired', {
      mechanicName: item.mechanicName,
      skillName: item.skillName,
      date: formatDate(item.expiresAt),
    });
  }
  return t('certificateGate.missing', {
    mechanicName: item.mechanicName,
    skillName: item.skillName,
  });
}

export function CertificateOverrideDialog({
  open,
  submitting,
  blocked,
  onClose,
  onSubmit,
}: CertificateOverrideDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const canOverride = useAuthStore((s) => s.hasPermission(SKILLS_CERTIFICATE_OVERRIDE_PERMISSION));
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  // Reset in the render that opens the dialog, not in an effect: an effect runs
  // after paint, so a reopened dialog could show the previous attempt's draft and
  // alerts for a frame, and it would discard input typed before it flushed.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setReason('');
      setReasonError(false);
      setSubmitError(false);
      setForbidden(false);
    }
  }

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const text = reason.trim();
    if (text.length === 0 || text.length > REASON_MAX) {
      setReasonError(true);
      return;
    }
    setSubmitError(false);
    setForbidden(false);
    try {
      await onSubmit(text);
    } catch (error) {
      if (isCertificateOverrideForbiddenError(error)) {
        setForbidden(true);
        return;
      }
      setSubmitError(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('certificateGate.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('certificateGate.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {blocked.map((item) => (
            <Typography key={`${item.mechanicUserId}-${item.skillId}`} variant="body2">
              {blockedLine(item, t)}
            </Typography>
          ))}
          {canOverride ? (
            <>
              {forbidden && <Alert severity="error">{t('certificateGate.forbidden')}</Alert>}
              {submitError && !forbidden && <Alert severity="error">{t('detail.statusChangeFailed')}</Alert>}
              <TextField
                label={t('certificateGate.reason')}
                helperText={
                  reasonError ? t('certificateGate.reasonRequired') : t('certificateGate.reasonHelper')
                }
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                  setReasonError(false);
                }}
                error={reasonError}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: REASON_MAX }}
              />
            </>
          ) : (
            <Alert severity="warning">{t('certificateGate.forbidden')}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        {canOverride && (
          <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
            {t('certificateGate.confirm')}
          </Button>
        )}
      </DialogActions>
    </ResponsiveDialog>
  );
}
